import React, { useState, useEffect, useRef } from 'react';
import styles from './ProductDetail.module.css';
import { productsApi } from '../../../../../api/products/productsApi';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const ProductDetail = ({ initialItemCode, onBack }) => {
  const [itemCode, setItemCode] = useState(initialItemCode || '');
  const [searchInput, setSearchInput] = useState(initialItemCode || '');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [productDetails, setProductDetails] = useState(null);
  const [stockLocations, setStockLocations] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [monthlySales, setMonthlySales] = useState([]);
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(getCurrentFinancialYear());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const searchRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Get current financial year (April to March)
  function getCurrentFinancialYear() {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1; // 1-12
    
    // If month is Jan-Mar, FY is (year-1)-year
    // If month is Apr-Dec, FY is year-(year+1)
    if (month >= 1 && month <= 3) {
      return `${year - 1}-${year}`;
    } else {
      return `${year}-${year + 1}`;
    }
  }

  // Convert financial year to start year for API
  function getStartYearFromFY(financialYear) {
    return parseInt(financialYear.split('-')[0]);
  }

  useEffect(() => {
    if (itemCode) {
      fetchProductData();
    }
  }, [itemCode]);

  useEffect(() => {
    if (itemCode) {
      fetchMonthlySales();
    }
  }, [itemCode, selectedFinancialYear]);

  // Debounced search for suggestions
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchInput.trim().length >= 2) {
        fetchSearchSuggestions();
      } else {
        setSearchSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchInput]);

  // Close suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        searchRef.current && 
        !searchRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSearchSuggestions = async () => {
    try {
      const suggestions = await productsApi.searchProducts(searchInput.trim());
      setSearchSuggestions(suggestions || []);
      setShowSuggestions(true);
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      setSearchSuggestions([]);
    }
  };

  const fetchProductData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 Fetching product details for:', itemCode);

      const [details, locations, customers] = await Promise.all([
        productsApi.getProductDetails(itemCode),
        productsApi.getStockLocations(itemCode),
        productsApi.getTopCustomers(itemCode),
      ]);

      setProductDetails(details);
      setStockLocations(locations || []);
      setTopCustomers(customers || []);

      console.log('✅ Product data loaded successfully');
    } catch (err) {
      console.error('❌ Error fetching product data:', err);
      setError('Failed to load product details');
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlySales = async () => {
    try {
      console.log('📊 Fetching monthly sales for:', itemCode, 'FY:', selectedFinancialYear);

      const startYear = getStartYearFromFY(selectedFinancialYear);
      const sales = await productsApi.getMonthlySalesByFinancialYear(itemCode, startYear);
      setMonthlySales(sales || []);

      console.log('✅ Monthly sales loaded:', sales?.length, 'records');
    } catch (err) {
      console.error('❌ Error fetching monthly sales:', err);
    }
  };

  const handleSearch = () => {
    if (searchInput.trim()) {
      setItemCode(searchInput.trim());
      setSearchInput(searchInput.trim());
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchInput(suggestion.itemCode);
    setItemCode(suggestion.itemCode);
    setShowSuggestions(false);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setShowSuggestions(false);
  };

  const formatCurrency = (amount) => {
    return '₹' + (amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  // Prepare chart data
  const getChartData = () => {
    const monthNames = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];
    const salesByMonth = new Array(12).fill(0);

    monthlySales.forEach(sale => {
      // Financial year months: Apr=0, May=1, ..., Mar=11
      const fyMonthIndex = sale.financialMonthIndex;
      if (fyMonthIndex >= 0 && fyMonthIndex < 12) {
        salesByMonth[fyMonthIndex] = sale.totalSales || 0;
      }
    });

    return {
      labels: monthNames,
      datasets: [
        {
          label: `Sales FY ${selectedFinancialYear} (₹)`,
          data: salesByMonth,
          backgroundColor: '#2563eb',
          borderColor: '#1d4ed8',
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return formatCurrency(context.parsed.y);
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return '₹' + value.toLocaleString('en-IN');
          }
        }
      },
      x: {
        grid: {
          display: false,
        }
      }
    }
  };

  const getFinancialYearOptions = () => {
    const options = [];
    const currentFY = getCurrentFinancialYear();
    const currentStartYear = getStartYearFromFY(currentFY);
    
    // Start from 2024-25 up to current FY
    for (let year = 2024; year <= currentStartYear; year++) {
      options.push(`${year}-${year + 1}`);
    }
    
    return options.reverse(); // Show most recent first
  };

  return (
    <div className={styles.wrapper}>
      {/* Search Bar with Suggestions */}
      <div className={styles.searchSection}>
        <div className={styles.searchRow}>
          <div className={styles.searchInputWrapper} ref={searchRef}>
            <span className={styles.searchIcon}>🔍</span>
            <input
              type="text"
              placeholder="Search by CAT No, Item Name, or CAS No..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              onFocus={() => searchInput.trim().length >= 2 && setShowSuggestions(true)}
              className={styles.searchInput}
            />
            {searchInput && (
              <button onClick={handleClearSearch} className={styles.clearSearchButton} title="Clear search">
                ✕
              </button>
            )}
            
            {/* Search Suggestions Dropdown */}
            {showSuggestions && searchSuggestions.length > 0 && (
              <div className={styles.suggestionsDropdown} ref={suggestionsRef}>
                {searchSuggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className={styles.suggestionItem}
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <div className={styles.suggestionCode}>{suggestion.itemCode}</div>
                    <div className={styles.suggestionName}>{suggestion.itemName}</div>
                    {suggestion.casNo && (
                      <div className={styles.suggestionCas}>CAS: {suggestion.casNo}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <button onClick={handleSearch} disabled={!searchInput.trim()} className={styles.searchButton}>
            Search
          </button>
        </div>
      </div>

      {loading && (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} />
          <div className={styles.loadingText}>Loading product details...</div>
        </div>
      )}

      {error && (
        <div className={styles.errorMessage}>{error}</div>
      )}

      {productDetails && (
        <>
          {/* Product Details Card */}
          <div className={styles.card}>
            <h2 className={styles.cardHeader}>
              Product Details - {productDetails.itemName || 'N/A'}
            </h2>
            
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Basic Information</h3>
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th className={styles.th}>Field</th>
                      <th className={styles.th}>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className={styles.tr}>
                      <td className={`${styles.td} ${styles.fieldName}`}>Item Code</td>
                      <td className={styles.td}>{productDetails.itemCode}</td>
                    </tr>
                    <tr className={styles.tr}>
                      <td className={`${styles.td} ${styles.fieldName}`}>Item Name</td>
                      <td className={styles.td}>{productDetails.itemName}</td>
                    </tr>
                    <tr className={styles.tr}>
                      <td className={`${styles.td} ${styles.fieldName}`}>Stock</td>
                      <td className={styles.td}>{productDetails.stock?.toFixed(2) || '0.00'}</td>
                    </tr>
                    <tr className={styles.tr}>
                      <td className={`${styles.td} ${styles.fieldName}`}>Item Type</td>
                      <td className={styles.td}>{productDetails.itemType || 'N/A'}</td>
                    </tr>
                    <tr className={styles.tr}>
                      <td className={`${styles.td} ${styles.fieldName}`}>CAS No</td>
                      <td className={styles.td}>{productDetails.casNo || 'N/A'}</td>
                    </tr>
                    <tr className={styles.tr}>
                      <td className={`${styles.td} ${styles.fieldName}`}>Created Date</td>
                      <td className={styles.td}>{formatDate(productDetails.createDate)}</td>
                    </tr>
                    <tr className={styles.tr}>
                      <td className={`${styles.td} ${styles.fieldName}`}>Updated Date</td>
                      <td className={styles.td}>{formatDate(productDetails.updateDate)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>KPI Summary</h3>
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th className={styles.th}>Field</th>
                      <th className={styles.th}>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className={styles.tr}>
                      <td className={`${styles.td} ${styles.fieldName}`}>Total Revenue Generated</td>
                      <td className={styles.td}>{formatCurrency(productDetails.totalRevenue)}</td>
                    </tr>
                    <tr className={styles.tr}>
                      <td className={`${styles.td} ${styles.fieldName}`}>Units Sold</td>
                      <td className={styles.td}>{productDetails.unitsSold?.toFixed(2) || '0.00'}</td>
                    </tr>
                    <tr className={styles.tr}>
                      <td className={`${styles.td} ${styles.fieldName}`}>Number of Customers</td>
                      <td className={styles.td}>{productDetails.numCustomers || 0}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Top Customers Card */}
          <div className={styles.card}>
            <h2 className={styles.cardHeader}>Top Customers</h2>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.th}>Customer Code</th>
                    <th className={styles.th}>Customer Name</th>
                    <th className={styles.th}>Total Spent (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {topCustomers.length > 0 ? (
                    topCustomers.map((customer, index) => (
                      <tr key={index} className={styles.tr}>
                        <td className={styles.td}>{customer.customerCode}</td>
                        <td className={styles.td}>{customer.customerName}</td>
                        <td className={styles.td}>{formatCurrency(customer.totalSpent)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className={styles.td} colSpan="3" style={{ textAlign: 'center' }}>
                        No customer data available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Stock Locations Card */}
          {/* <div className={styles.card}>
            <h2 className={styles.cardHeader}>Stock Locations</h2>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.th}>Location</th>
                    <th className={styles.th}>Warehouse Name</th>
                    <th className={styles.th}>Quantity</th>
                    <th className={styles.th}>Batch Number</th>
                    <th className={styles.th}>Vendor Batch</th>
                    <th className={styles.th}>COA</th>
                  </tr>
                </thead>
                <tbody>
                  {stockLocations.length > 0 ? (
                    stockLocations.map((location, index) => (
                      <tr key={index} className={styles.tr}>
                        <td className={styles.td}>{location.location || 'N/A'}</td>
                        <td className={styles.td}>{location.warehouseName || 'N/A'}</td>
                        <td className={styles.td}>{location.quantity?.toFixed(2) || '0.00'}</td>
                        <td className={styles.td}>{location.batchNumber || 'N/A'}</td>
                        <td className={styles.td}>{location.vendorBatch || 'N/A'}</td>
                        <td className={styles.td}>{location.coa || 'N/A'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className={styles.td} colSpan="6" style={{ textAlign: 'center' }}>
                        No stock location data available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div> */}

          {/* Additional Information Card */}
          <div className={styles.card}>
            <h2 className={styles.cardHeader}>Additional Information</h2>
            <div className={styles.additionalInfo}>
              <div className={styles.additionalRow}>
                <strong>IUPAC Name:</strong> {productDetails.iupacName || 'N/A'}
              </div>
              <div className={styles.additionalRow}>
                <strong>Synonyms:</strong> {productDetails.synonyms || 'N/A'}
              </div>
              <div className={styles.additionalRow}>
                <strong>Applications:</strong> {productDetails.applications || 'N/A'}
              </div>
              <div className={styles.additionalRow}>
                <strong>Structure:</strong> {productDetails.structure || 'N/A'}
              </div>
            </div>
          </div>

          {/* Monthly Sales Chart with Financial Year */}
          <div className={styles.card}>
            <div className={styles.chartHeaderContainer}>
              <h2 className={`${styles.cardHeader} ${styles.chartCardHeader}`}>
                Monthly Sales
              </h2>
              <div className={styles.yearSelector}>
                <label className={styles.yearLabel}>Financial Year:</label>
                <select
                  value={selectedFinancialYear}
                  onChange={(e) => setSelectedFinancialYear(e.target.value)}
                  className={styles.yearSelect}
                >
                  {getFinancialYearOptions().map(fy => (
                    <option key={fy} value={fy}>{fy}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className={styles.chartContainer}>
              <Bar data={getChartData()} options={chartOptions} />
            </div>
          </div>
        </>
      )}

      {!productDetails && !loading && (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📦</div>
          <div className={styles.emptyText}>Enter a CAT No to view product details</div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;